// eslint-disable-next-line import/no-cycle
import { portalGate as portalGateToForeground } from '../../foreground/portalGates/contentFrame/from/reactNativeWebview';
import delay from '../../utils/delay';
import { getATagAncestor } from './getATagAncestor';
import { getScrollingManager } from './getScrollingManager';
import { HorizontalPaginatedScrollingManager } from './HorizontalPaginatedScrollingManager';
import { isURLToImage } from './setupAContainingImg';
import type { MobileContentFrameWindow } from './types';
import { VerticalPaginatedScrollingManager } from './VerticalPaginatedScrollingManager';

declare let window: MobileContentFrameWindow;

enum ImageHighlightState {
  NotHighlight = 'NotHighlight',
  Highlight = 'Highlight',
  ActiveHighlight = 'ActiveHighlight',
}

const getImageHighlightState = (img: HTMLImageElement): ImageHighlightState => {
  if (
    img.classList.contains('rw-image-highlight') &&
    img.classList.contains('rw-image-highlight--active')
  ) {
    return ImageHighlightState.ActiveHighlight;
  }
  if (img.classList.contains('rw-image-highlight')) {
    return ImageHighlightState.Highlight;
  }
  return ImageHighlightState.NotHighlight;
};

export const selectImageElementToHighlight = async (img: HTMLImageElement) => {
  const windowSelection = window.getSelection();
  if (windowSelection === null) {
    throw new Error('window.getSelection() returned null');
  }
  windowSelection.removeAllRanges();

  const imgRange = new Range();
  imgRange.setStartBefore(img);
  imgRange.setEndAfter(img);
  windowSelection.addRange(imgRange);

  await delay(50);
};

// Prevent duplicate event listeners
let areListenersAdded = false;

export default ({ os }: { os: string; }) => {
  const longPressMaxDifference = os === 'ios' ? 10 : 5;

  let presstimer: ReturnType<typeof setTimeout> | null = null;
  let doubleTapTimer: ReturnType<typeof setTimeout> | null = null;
  // we need this variable to save current highlight state before any touch event is applied to get reliable value
  let currentImageHighlightState: ImageHighlightState | null = null;
  let longPressStartX = 0;
  let longPressStartY = 0;
  let lastPressedImageId: string;

  return function imagePressingInitializer() {
    const images = document.querySelectorAll<HTMLImageElement>('#document-text-content img');
    images.forEach((img, i) => {
      img.id = `img-index-${i}`;
    });

    if (areListenersAdded) {
      return;
    }
    areListenersAdded = true;

    document.addEventListener(
        'touchstart',
        (e) => {
          // claude eslint: tagName guard below confirms this is an HTMLImageElement
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const img = e.target as HTMLImageElement;
          if (img.tagName !== 'IMG') {
            return;
          }

          function elementIsCoverImage(element: HTMLElement) {
            const id = element.id.toLowerCase();
            const classList = element.classList.toString().toLowerCase();
            const chunkId = (element.getAttribute('data-chunk-id') ?? '').toLowerCase();
            const chunkFilename = (element.getAttribute('data-chunk-filename') ?? '').toLowerCase();
            const chunkEpubToc = (element.getAttribute('data-rw-epub-toc') ?? '').toLowerCase();
            const propertiesToTest = [id, classList, chunkId, chunkFilename, chunkEpubToc];

            for (const property of propertiesToTest) {
              if (property.includes('title') || property.includes('cover')) {
                return true;
              }
            }
            return false;
          }
          // Check if this image has any mention of class or title in all parent components up to the container
          let parentElement: HTMLElement | null = img;
          const documentTextContent: HTMLElement | null = document.querySelector<HTMLElement>('#document-text-content');
          if (!documentTextContent) {
            throw new Error('No document text content node found');
          }
          while (parentElement && parentElement !== documentTextContent) {
            if (elementIsCoverImage(parentElement)) {
              return;
            }
            parentElement = parentElement.parentElement;
          }


          currentImageHighlightState ??= getImageHighlightState(img);

          if (presstimer === null) {
            const touch = e.changedTouches[0];
            longPressStartX = touch.pageX;
            longPressStartY = touch.pageY;

            presstimer = setTimeout(() => {
              // Long press event
              presstimer = null;

              const imageHighlightState = currentImageHighlightState;
              currentImageHighlightState = null;
              if (imageHighlightState === ImageHighlightState.NotHighlight) {
                selectImageElementToHighlight(img);
                portalGateToForeground.emit('highlight-image');
                return;
              }
              if (imageHighlightState === ImageHighlightState.Highlight) {
                const highlightId = img.getAttribute('data-highlight-id');
                portalGateToForeground.emit('highlight-clicked', {
                  id: highlightId,
                });
                return;
              }
              if (imageHighlightState === ImageHighlightState.ActiveHighlight) {
                portalGateToForeground.emit('image_clicked', {
                  src: img.src,
                  id: img.id,
                  highlighted: true,
                });
              }
            }, 300);
          }
        },
        { passive: true },
      );

    const onDoubleClick = (img: HTMLImageElement) => {
      const imageHighlightState = currentImageHighlightState;
      currentImageHighlightState = null;
      if (imageHighlightState === ImageHighlightState.NotHighlight) {
        selectImageElementToHighlight(img);
        portalGateToForeground.emit('highlight-image');
        return;
      }
      if (imageHighlightState === ImageHighlightState.Highlight) {
        const highlightId = img.getAttribute('data-highlight-id');
        portalGateToForeground.emit('highlight-clicked', {
          id: highlightId,
        });
        return;
      }
      if (imageHighlightState === ImageHighlightState.ActiveHighlight) {
        const highlightId = img.getAttribute('data-highlight-id');
        portalGateToForeground.emit('highlight-double-clicked', {
          id: highlightId,
        });
      }
    };

    document.addEventListener(
        'touchend',
        (e) => {
          // claude eslint: tagName guard below confirms this is an HTMLImageElement
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const img = e.target as HTMLImageElement;
          if (img.tagName !== 'IMG') {
            return;
          }

          const scrollManager = getScrollingManager('touchEndImagePresser');
          const isPaginatedMode = scrollManager instanceof VerticalPaginatedScrollingManager || scrollManager instanceof HorizontalPaginatedScrollingManager;
          if (!isPaginatedMode) {
            // TODO: fix. This broke double-tap image highlighting; https://linear.app/readwise/issue/RW-34910/bug-image-highlighting-behavior-regressed-to-be-more-difficult
            // e.stopPropagation();
          } else {
            const { clientX } = e.changedTouches[0];
            if (
              clientX <= scrollManager.leftClickAreaWidth ||
              clientX >= scrollManager.rightClickAreaWidth
            ) {
              if (doubleTapTimer) {
                clearTimeout(doubleTapTimer);
              }
              if (presstimer) {
                clearTimeout(presstimer);
              }
              presstimer = null;
              doubleTapTimer = null;
              return;
            }
          }

          if (presstimer !== null) {
            clearTimeout(presstimer);
            presstimer = null;

            if (doubleTapTimer === null || lastPressedImageId !== img.id) {
              doubleTapTimer = setTimeout(() => {
                lastPressedImageId = img.id;
                doubleTapTimer = null;

                const imageHighlightState = currentImageHighlightState;
                currentImageHighlightState = null;
                // Press event
                if (imageHighlightState === ImageHighlightState.NotHighlight) {
                  const aTagAncestor = getATagAncestor(img);
                  if (aTagAncestor && aTagAncestor.href && !isURLToImage(aTagAncestor.href)) {
                    portalGateToForeground.emit('image_clicked', {
                      src: img.src,
                      id: img.id,
                      highlighted: false,
                      isLink: true,
                    });
                    window.portalGateToForeground.emit('link-clicked', aTagAncestor.href);
                  } else {
                    portalGateToForeground.emit('image_clicked', {
                      src: img.src,
                      id: img.id,
                      highlighted: false,
                    });
                  }
                } else if (imageHighlightState === ImageHighlightState.Highlight) {
                  const highlightId = img.getAttribute('data-highlight-id');
                  portalGateToForeground.emit('highlight-clicked', {
                    id: highlightId,
                  });
                } else if (imageHighlightState === ImageHighlightState.ActiveHighlight) {
                  const highlightId = img.getAttribute('data-highlight-id') || undefined;
                  portalGateToForeground.emit('image_clicked', {
                    src: img.src,
                    id: img.id,
                    highlighted: true,
                    highlightId,
                  });
                }
              }, 250);
            } else {
              clearTimeout(doubleTapTimer);
              doubleTapTimer = null;
              onDoubleClick(img);
            }
          }
        },
        { passive: true },
      );

    document.addEventListener(
        'touchmove',
        (e) => {
          // claude eslint: tagName guard below confirms this is an HTMLImageElement
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const img = e.target as HTMLImageElement;
          if (img.tagName !== 'IMG') {
            return;
          }

          const touch = e.changedTouches[0];
          const diffX = Math.abs(longPressStartX - touch.pageX);
          const diffY = Math.abs(longPressStartY - touch.pageY);

          if (diffX >= longPressMaxDifference || diffY >= longPressMaxDifference) {
            if (presstimer) {
              clearTimeout(presstimer);
              presstimer = null;
              currentImageHighlightState = null;
            }
          }
        },
        { passive: true },
      );
  };
};
